import { useState, useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import Breadcrumbs from '@/pages/Blog/components/Breadcrumbs';
import JsonLd, { breadcrumbSchema } from '@/components/base/JsonLd';
import useSeo, { SITE_URL, OG_IMAGE } from '@/hooks/useSeo';
import { socialLinks } from '@/mocks/socialLinks';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSeo({
    title: 'Contact Us | TNT Coach AI',
    description: 'Have a question, suggestion, or want to say hello? Contact the TNT Coach AI team and we will get back to you within 24 hours on business days.',
    canonical: `${SITE_URL}/contact`,
    url: `${SITE_URL}/contact`,
    image: OG_IMAGE,
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setErrorMessage('');

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          urlEncoded.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d918ljobhec1he3opqcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      if (!response.ok) throw new Error('Submission failed');

      setFormStatus('success');
      form.reset();
    } catch {
      setFormStatus('error');
      setErrorMessage('Something went wrong. Please try again or email us directly at support@tntcoach.ai.');
    }
  };

  return (
    <div className="min-h-screen bg-midnight">
      <JsonLd
        data={breadcrumbSchema([
          { name: 'Home', url: `${SITE_URL}/` },
          { name: 'Contact', url: `${SITE_URL}/contact` },
        ])}
      />
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-32 pb-12">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Contact' }]} />
              <div className="text-center">
                <p className="text-champagne-gold text-sm font-medium tracking-wider uppercase mb-3">Get In Touch</p>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-silver-grey mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Contact Us
                </h1>
                <p className="text-silver-grey/60 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
                  Have a question, suggestion, or just want to say hello? Fill out the form below and we will get back to you as soon as possible.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="border-t border-royal-navy/30" />
        </div>

        {/* Contact Form + Info */}
        <section className="py-12 md:py-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
                {/* Form */}
                <div className="lg:col-span-3">
                  <form
                    data-readdy-form
                    id="contact-form"
                    onSubmit={handleSubmit}
                    className="space-y-6"
                  >
                    {/* Honeypot */}
                    <div className="contact-phone-alt-wrapper" aria-hidden="true">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-silver-grey text-sm font-medium mb-2">
                          Name <span className="text-champagne-gold">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          placeholder="Your name"
                          className="w-full px-4 py-3 rounded-lg bg-warm-white border border-royal-navy/20 text-midnight placeholder:text-taupe-grey focus:outline-none focus:border-champagne-gold/60 focus:ring-1 focus:ring-champagne-gold/30 transition-all duration-300 text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-silver-grey text-sm font-medium mb-2">
                          Email <span className="text-champagne-gold">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          placeholder="you@example.com"
                          className="w-full px-4 py-3 rounded-lg bg-warm-white border border-royal-navy/20 text-midnight placeholder:text-taupe-grey focus:outline-none focus:border-champagne-gold/60 focus:ring-1 focus:ring-champagne-gold/30 transition-all duration-300 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-silver-grey text-sm font-medium mb-2">
                        Subject <span className="text-champagne-gold">*</span>
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        placeholder="What is this about?"
                        className="w-full px-4 py-3 rounded-lg bg-warm-white border border-royal-navy/20 text-midnight placeholder:text-taupe-grey focus:outline-none focus:border-champagne-gold/60 focus:ring-1 focus:ring-champagne-gold/30 transition-all duration-300 text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-silver-grey text-sm font-medium mb-2">
                        Message <span className="text-champagne-gold">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        maxLength={500}
                        placeholder="Tell us what's on your mind..."
                        className="w-full px-4 py-3 rounded-lg bg-warm-white border border-royal-navy/20 text-midnight placeholder:text-taupe-grey focus:outline-none focus:border-champagne-gold/60 focus:ring-1 focus:ring-champagne-gold/30 transition-all duration-300 text-sm resize-none"
                      />
                      <p className="text-silver-grey/40 text-xs mt-1.5">
                        Maximum 500 characters
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full gold-gradient text-midnight font-semibold text-sm hover:opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <i className="ri-send-plane-line" />
                        </>
                      )}
                    </button>

                    {/* Status Messages */}
                    {formStatus === 'success' && (
                      <div className="flex items-start gap-3 p-4 rounded-lg bg-champagne-gold/10 border border-champagne-gold/30 animate-fade-in">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-line text-champagne-gold" />
                        </div>
                        <div>
                          <p className="text-champagne-gold text-sm font-medium">Message sent!</p>
                          <p className="text-silver-grey/70 text-sm mt-1">Thanks for reaching out. We will get back to you within 24 hours.</p>
                        </div>
                      </div>
                    )}

                    {formStatus === 'error' && (
                      <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30 animate-fade-in">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-error-warning-line text-red-400" />
                        </div>
                        <div>
                          <p className="text-red-400 text-sm font-medium">Oops, something went wrong</p>
                          <p className="text-silver-grey/70 text-sm mt-1">{errorMessage}</p>
                        </div>
                      </div>
                    )}
                  </form>
                </div>

                {/* Side Info */}
                <div className="lg:col-span-2">
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-lg font-semibold text-champagne-gold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                        Other Ways to Reach Us
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-royal-navy/30 flex items-center justify-center flex-shrink-0">
                            <i className="ri-mail-line text-champagne-gold text-lg" />
                          </div>
                          <div>
                            <p className="text-silver-grey text-sm font-medium">Email</p>
                            <a
                              href="mailto:support@tntcoach.ai"
                              className="text-silver-grey/70 text-sm hover:text-champagne-gold transition-colors duration-300"
                            >
                              support@tntcoach.ai
                            </a>
                          </div>
                        </div>

                        {socialLinks.map((social) => (
                          <div key={social.platform} className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-royal-navy/30 flex items-center justify-center flex-shrink-0">
                              <i className={`${social.icon} text-champagne-gold text-lg`} />
                            </div>
                            <div>
                              <p className="text-silver-grey text-sm font-medium">
                                {social.platform === 'X' ? 'X (Twitter)' : social.platform}
                              </p>
                              <a
                                href={social.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-silver-grey/70 text-sm hover:text-champagne-gold transition-colors duration-300 break-all"
                              >
                                {social.handle}
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-royal-navy/30 pt-8">
                      <h3 className="text-lg font-semibold text-champagne-gold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                        Response Time
                      </h3>
                      <p className="text-silver-grey/70 text-sm leading-relaxed">
                        We typically respond within 24 hours during business days. For urgent matters, email is the fastest way to reach us.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back link */}
        <section className="pb-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-5xl mx-auto">
              <a
                href="/"
                className="inline-flex items-center gap-2 text-champagne-gold hover:text-champagne-gold/80 transition-colors duration-300 text-sm"
              >
                <i className="ri-arrow-left-line" />
                Back to Home
              </a>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}